import React, { useState } from 'react';

import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  BoxProps,
  Button,
  Flex,
  List,
  Modal,
  Paper,
  Space,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleNumber1Filled,
  IconCircleNumber2Filled,
  IconCircleNumber3Filled,
  IconCircleNumber4Filled,
  IconCircleNumber5Filled,
  IconExternalLink,
  IconTrash
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { DeauthorizedBadge } from 'components/DeauthorizedBadge';
import { WhatsAppPhoneNumberTile } from 'components/WhatsAppAccountHandler/WhatsAppPhoneNumberTile';
import { useTranslation } from 'react-i18next';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_BY_WHATSAPP_ACCOUNT_ID_QUERY_KEY } from 'shared/constants/query-keys';
import { WhatsAppAccount } from 'shared/interfaces/WhatsAppAccount';

interface WhatsAppAccountProps extends BoxProps {
  account: WhatsAppAccount;
}

export function WhatsAppAccountCard({ account, ...boxProps }: WhatsAppAccountProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [deleteAccountOpened, setDeleteAccountOpened] = useState(false);

  const manageAccountOnMetaLink = `https://business.facebook.com/wa/manage/home/?waba_id=${account.whatsAppId}`;
  const deleteAccountOnMetaLink = `https://business.facebook.com/settings/whatsapp-business-accounts/${account.whatsAppId}?tab=whatsapp_settings`;

  const smartAssistantsQuery = useQuery({
    queryKey: [SMART_ASSISTANT_BY_WHATSAPP_ACCOUNT_ID_QUERY_KEY, account._id],
    queryFn: () => SmartAssistantService.getSmartAssistantsByWhatsAppAccountId(account._id)
  });

  return (
    <>
      <Box {...boxProps}>
        <Flex
          direction="row"
          justify="space-between"
          align="center"
          py={5}
          style={{ borderBottom: `1px solid ${theme.colors.gray[3]}` }}>
          <Flex direction="row" align="center">
            <Title order={6}>{account.name}</Title>
            <Space w={15} />
            <Anchor href={manageAccountOnMetaLink} target="_blank" underline fz="xs">
              <Flex direction="row" align="center">
                {t('WHATSAPP.MANAGE_ACCOUNT')}
                <Space w={5} />
                <IconExternalLink style={{ width: 12, height: 12 }} />
              </Flex>
            </Anchor>
            {account.accessRevokedDate && <DeauthorizedBadge />}
          </Flex>

          <ActionIcon color="red" onClick={() => setDeleteAccountOpened(true)}>
            <IconTrash size={22} />
          </ActionIcon>
        </Flex>
        <Space h={10} />
        <Box>
          {account.phoneNumbers.map((phoneNumber, idx) => (
            <WhatsAppPhoneNumberTile
              key={idx}
              mt={idx === 0 ? 0 : 5}
              mb={idx === account.phoneNumbers.length - 1 ? 0 : 5}
              account={account}
              phoneNumber={phoneNumber}
            />
          ))}
        </Box>
      </Box>
      <Modal
        opened={deleteAccountOpened}
        onClose={() => setDeleteAccountOpened(false)}
        title={t('WHATSAPP.DELETE.ACCOUNT.TITLE')}
        size="lg"
        centered>
        {smartAssistantsQuery.data && (
          <Alert mb="xs" icon={<IconAlertCircle />} title={t('WHATSAPP.DELETE.ACCOUNT.WARNING.TITLE')} color="red">
            <Text fz="md">{t('WHATSAPP.DELETE.ACCOUNT.WARNING.DESCRIPTION')}</Text>
          </Alert>
        )}
        <Paper bg="gray.1" p="md">
          <Text fz="md">{t('WHATSAPP.DELETE.ACCOUNT.INSTRUCTIONS')}</Text>
          <List size="md" mt="xs" ml="md" type="ordered">
            <List.Item icon={<IconCircleNumber1Filled />}>{t('WHATSAPP.DELETE.ACCOUNT.STEP_1')}</List.Item>
            <List.Item icon={<IconCircleNumber2Filled />}>{t('WHATSAPP.DELETE.ACCOUNT.STEP_2')}</List.Item>
            <List.Item icon={<IconCircleNumber3Filled />}>{t('WHATSAPP.DELETE.ACCOUNT.STEP_3')}</List.Item>
            <List.Item icon={<IconCircleNumber4Filled />}>{t('WHATSAPP.DELETE.ACCOUNT.STEP_4')}</List.Item>
            <List.Item icon={<IconCircleNumber5Filled />}>{t('WHATSAPP.DELETE.ACCOUNT.STEP_5')}</List.Item>
          </List>
        </Paper>
        <Flex justify="flex-end">
          <Button
            mt="md"
            variant="outline"
            component="a"
            href={deleteAccountOnMetaLink}
            target="_blank"
            rightIcon={<IconExternalLink />}>
            {t('WHATSAPP.DELETE.ACCOUNT.CTA')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
